import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchServersAPI, updateServerAPI } from '../services/serverService';
import AddServerModal from '../components/AddServerModal';
import DisableServerModal from '../components/DisableServerModal';
import GlobalBroadcastModal from '../components/GlobalBroadcastModal';
import { FaEdit, FaPowerOff, FaArchive, FaInfoCircle, FaExclamationTriangle, FaRobot } from 'react-icons/fa';

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

import { hasPermission } from '../config/permissions';
import { useAuth } from '../context/AuthContext';

const ServerInfoModal = ({ server, closeModal }) => {
    // --- CHECK LIMIT LOGIC ---
    const totalStaff = (server.total_admins || 0) + (server.total_employees || 0);
    const maxUsers = server.maxUsers || 0;
    const isOverLimit = maxUsers > 0 && totalStaff > maxUsers;

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }} onClick={closeModal}>
            <div style={{ backgroundColor: 'white', width: '400px', borderRadius: '8px', padding: '25px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 20px 0', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>{server.server_name} Overview</h3>
                
                {/* Unified Total Staff Box */}
                <div style={{ 
                    backgroundColor: isOverLimit ? '#fff5f5' : '#f7fafc', 
                    border: isOverLimit ? '1px solid #feb2b2' : '1px solid #e2e8f0',
                    padding: '20px', borderRadius: '8px', textAlign: 'center', marginBottom: '20px' 
                }}>
                    <div style={{ fontSize: '12px', color: isOverLimit ? '#c53030' : '#4a5568', textTransform: 'uppercase', fontWeight: 'bold' }}>Total Staff Usage</div>
                    
                    <div style={{ fontSize: '36px', fontWeight: 'bold', color: isOverLimit ? '#9b2c2c' : '#2d3748', margin: '10px 0' }}>
                        {totalStaff} <span style={{ fontSize: '18px', color: isOverLimit ? '#e53e3e' : '#a0aec0' }}>/ {maxUsers > 0 ? maxUsers : '∞'}</span>
                    </div>
                    
                    {isOverLimit && <div style={{ fontSize: '13px', color: '#e53e3e', fontWeight: 'bold', marginBottom: '10px' }}>⚠️ License Limit Exceeded</div>}
                    
                    {/* Small Breakdown */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '10px', fontSize: '13px', color: '#718096', borderTop: isOverLimit ? '1px solid #fed7d7' : '1px solid #edf2f7', paddingTop: '10px' }}>
                        <span><strong>{server.total_admins || 0}</strong> Admins</span>
                        <span>|</span>
                        <span><strong>{server.total_employees || 0}</strong> Employees</span>
                    </div>
                </div>

                <div style={{ backgroundColor: '#ebf8ff', padding: '15px', borderRadius: '6px', border: '1px solid #90cdf4' }}>
                    <div style={{ fontSize: '12px', color: '#2b6cb0', textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '5px' }}>Contract Expiry</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#2c5282' }}>
                        {server.contractEndDate ? new Date(server.contractEndDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'No Date Set'}
                    </div>
                </div>

                <button onClick={closeModal} style={{ width: '100%', padding: '12px', marginTop: '20px', backgroundColor: '#e2e8f0', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', color: '#4a5568' }}>Close</button>
            </div>
        </div>
    );
};

const ServersPage = () => {
    const [servers, setServers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Automation Health State
    const [cronStatus, setCronStatus] = useState(null);
    
    // Modals State
    const [modalState, setModalState] = useState(null); 
    const [disableModalServer, setDisableModalServer] = useState(null); 
    const [infoModalServer, setInfoModalServer] = useState(null);
    const [isGlobalBroadcastModalOpen, setIsGlobalBroadcastModalOpen] = useState(false);

    const navigate = useNavigate(); 

    useEffect(() => { 
        loadServers(); 
        fetchAutomationHealth();
    }, []);

    const fetchAutomationHealth = async () => {
        try {
            const docRef = doc(db, 'supportMeta', 'cron_status');
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setCronStatus(docSnap.data());
            }
        } catch (error) {
            console.error("Failed to load automation health:", error);
        }
    };

    const loadServers = async () => {
        try {
            setIsLoading(true);
            const response = await fetchServersAPI();
            setServers(response.data.data.servers || []);
        } catch (error) {
            console.error("Failed to fetch servers", error);
            setServers([]); 
        } finally {
            setIsLoading(false);
        }
    };

    // --- HELPER LOGIC FOR HEALTH INDICATOR ---
    const renderHealthIndicator = () => {
        if (!cronStatus || !cronStatus.lastRun) {
            return <span style={{ color: '#718096' }}>Pending First Run</span>;
        }

        // Handle Firebase timestamp conversion
        const lastRunDate = cronStatus.lastRun.toDate ? cronStatus.lastRun.toDate() : new Date(cronStatus.lastRun);
        
        // Calculate difference in hours
        const diffHours = (new Date() - lastRunDate) / (1000 * 60 * 60);
        
        // If it ran within the last 26 hours, it's healthy. Otherwise, the CRON failed to trigger.
        const isHealthy = diffHours < 26;
        const formattedDate = lastRunDate.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

        return (
            <>
                <strong style={{ color: isHealthy ? '#38a169' : '#e53e3e' }}>
                    {isHealthy ? 'Active' : 'Error / Delayed'}
                </strong>
                <span style={{ fontSize: '11px', marginLeft: '6px', color: '#a0aec0' }}>
                    (Last: {formattedDate})
                </span>
            </>
        );
    };

    const handleOpenAddModal = () => { setModalState('add'); };
    
    const handleOpenEditModal = (e, server) => { 
        e.stopPropagation();
        setModalState(server); 
    };

    const handleOpenInfoModal = (e, server) => {
        e.stopPropagation();
        setInfoModalServer(server);
    }
    
    const handleCloseModal = () => { 
        setModalState(null);
        loadServers(); 
    };

    const formatAddress = (address) => {
        if (typeof address === 'string' && address.startsWith(',')) {
            return address.substring(1).trim(); 
        }
        return address;
    };

    const handleOpenDisableModal = (e, server) => {
        e.stopPropagation();
        setDisableModalServer(server);
    };

    const handleConfirmToggle = async (reason) => {
        if (!disableModalServer) return;
        
        const isCurrentlySuspended = disableModalServer.status === 'suspended';
        const newStatus = isCurrentlySuspended ? 'active' : 'suspended';
        
        try {
            await updateServerAPI(disableModalServer.server_id, { 
                status: newStatus,
                reason: reason 
            });
            alert(`Server ${isCurrentlySuspended ? 'Enabled' : 'Disabled'} successfully.`);
            setDisableModalServer(null);
            loadServers(); 
        } catch (error) {
            console.error("Failed to update server status", error);
            alert("Error: " + (error.response?.data?.message || error.message));
        }
    };

    const handleArchiveServer = async (e, server) => {
        e.stopPropagation();
        if (window.confirm(`Are you sure you want to ARCHIVE '${server.server_name}'?\n\nThis will remove it from this list and block all users. You can un-archive it later from the Archived Servers page.`)) {
            try {
                await updateServerAPI(server.server_id, { status: 'archived', reason: 'Archived by Super Admin' });
                alert('Server archived successfully.');
                loadServers();
            } catch (error) {
                alert("Failed to archive: " + (error.response?.data?.message || error.message));
            }
        }
    };

    // --- Contract Expiry Check (For Row Highlighting) ---
    const isExpiringSoon = (endDate) => {
        if (!endDate) return false;
        const diffTime = new Date(endDate) - new Date();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 90; 
    };

    const { userRole } = useAuth();
    const canManageAdmins = hasPermission(userRole, 'CAN_MANAGE_GLOBAL_ADMINS');
    const canDelete = hasPermission(userRole, 'CAN_DELETE_DATA');

    return (
        <div className="page-container">
            {modalState && (
                <AddServerModal 
                    closeModal={handleCloseModal} 
                    refreshList={loadServers}
                    serverToEdit={modalState !== 'add' ? modalState : null}
                />
            )}

            {disableModalServer && (
                <DisableServerModal 
                    server={disableModalServer}
                    isEnabling={disableModalServer.status === 'suspended'}
                    closeModal={() => setDisableModalServer(null)}
                    onConfirm={handleConfirmToggle}
                />
            )}

            {infoModalServer && (
                <ServerInfoModal server={infoModalServer} closeModal={() => setInfoModalServer(null)} />
            )}

            {isGlobalBroadcastModalOpen && (
                <GlobalBroadcastModal 
                    servers={servers} 
                    closeModal={() => setIsGlobalBroadcastModalOpen(false)} 
                />
            )}

            <div className="page-header" style={{ alignItems: 'flex-start' }}>
                <div>
                    <h1>Servers</h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#718096', marginTop: '5px' }}>
                        <FaRobot style={{ color: '#3182ce' }} />
                        <span>Nightly Auto-Sweep: {renderHealthIndicator()}</span>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    {canManageAdmins && (
                        <>
                            <button className="add-button" style={{ backgroundColor: '#2b6cb0' }} onClick={() => setIsGlobalBroadcastModalOpen(true)}>
                                Send Global Broadcast
                            </button>
                            <button className="add-button" onClick={handleOpenAddModal}>+ Add New Server</button>
                        </>
                    )}
                </div>
            </div>

            <div className="data-table-container">
                <table className="data-table">
                    <colgroup>
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '16%' }} />
                        <col style={{ width: '8%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '15%' }} />
                        <col style={{ width: '8%' }} />
                        <col style={{ width: '18%' }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Server ID</th>
                            <th>Company Name</th>
                            <th>Tier</th>
                            <th>Phone</th>
                            <th className="wrap-cell">Address</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan="7" className="no-data-cell">Loading...</td></tr>
                        ) : servers.length > 0 ? servers.map((server) => {
                            const isSuspended = server.status === 'suspended';
                            const expiring = isExpiringSoon(server.contractEndDate);
                            
                            // --- ROW WARNING LOGIC ---
                            const totalStaff = (server.total_admins || 0) + (server.total_employees || 0);
                            const maxUsers = server.maxUsers || 0;
                            const isOverLimit = maxUsers > 0 && totalStaff > maxUsers;
                            
                            return (
                                <tr 
                                    key={server.server_id} 
                                    style={{ 
                                        opacity: isSuspended ? 0.7 : 1, 
                                        cursor: 'pointer',
                                        // Red highlight for expiry, Yellow highlight for limits
                                        backgroundColor: expiring ? '#fff5f5' : (isOverLimit ? '#fffff0' : 'inherit')
                                    }}
                                    onClick={() => navigate(`/servers/${server.server_id}`)}
                                    className="clickable-row"
                                >
                                    <td>{server.server_id}</td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {/* Contract Expiry Warning (RED) */}
                                            {expiring && <FaExclamationTriangle style={{ color: '#e53e3e' }} title="Contract expiring soon or overdue" />}
                                            
                                            {/* License Limit Warning (YELLOW) */}
                                            {isOverLimit && <FaExclamationTriangle style={{ color: '#d69e2e' }} title="Staff license cap exceeded" />}
                                            
                                            <span style={{ fontWeight: (expiring || isOverLimit) ? 'bold' : 'normal'}}>{server.server_name}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span style={{ 
                                            padding: '4px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 'bold',
                                            backgroundColor: server.isLite ? '#edf2f7' : '#faf5ff',
                                            color: server.isLite ? '#4a5568' : '#553c9a',
                                            border: `1px solid ${server.isLite ? '#cbd5e0' : '#e9d8fd'}`
                                        }}>
                                            {server.isLite ? 'FREEMIUM' : 'PREMIUM'}
                                        </span>
                                    </td>
                                    <td>{server.phone_number}</td>
                                    <td className="wrap-cell">
                                        {formatAddress(server.company_address)}
                                    </td>
                                    <td>
                                        <span 
                                            className={!isSuspended ? 'status-active' : 'status-inactive'} 
                                            style={{ 
                                                backgroundColor: isSuspended ? '#e74c3c' : '#2ecc71',
                                                padding: '4px 8px', borderRadius: '4px', color: 'white',
                                                fontSize: '0.8rem', fontWeight: 'bold'
                                            }}
                                        >
                                            {isSuspended ? 'Suspended' : 'Active'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="actions-wrapper" style={{ display: 'flex', gap: '8px' }} onClick={e => e.stopPropagation()}>
                                            <button 
                                                className="action-button" 
                                                style={{ padding: '8px', fontSize: '1rem', background: '#ebf8ff', color: '#3182ce', border: '1px solid #90cdf4', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                onClick={(e) => handleOpenInfoModal(e, server)}
                                                title="View Usage Info"
                                            >
                                                <FaInfoCircle />
                                            </button>

                                            <button 
                                                className="action-button" 
                                                style={{ padding: '8px', fontSize: '1rem', background: 'transparent', color: '#4a5568', border: '1px solid #cbd5e0', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                onClick={(e) => handleOpenEditModal(e, server)}
                                                title="Edit Server"
                                            >
                                                <FaEdit />
                                            </button>

                                            <button 
                                                className={`action-button ${isSuspended ? 'success' : 'danger'}`}
                                                style={{ padding: '8px', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', border: 'none', color: 'white', backgroundColor: isSuspended ? '#2ecc71' : '#e53e3e' }}
                                                onClick={(e) => handleOpenDisableModal(e, server)}
                                                title={isSuspended ? "Re-activate Server" : "Suspend Server"}
                                            >
                                                <FaPowerOff />
                                            </button>

                                            {canDelete && (
                                                <button 
                                                    className="action-button" 
                                                    style={{ padding: '8px', fontSize: '1rem', backgroundColor: '#718096', color: 'white', border: 'none', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                    onClick={(e) => handleArchiveServer(e, server)}
                                                    title="Archive Server"
                                                >
                                                    <FaArchive />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        }) : (
                            <tr><td colSpan="7" className="no-data-cell">No Servers Found</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ServersPage;